import { useNavigate } from "react-router-dom";
import { 
  IoLocationOutline, 
  IoCalendarOutline, 
  IoTimeOutline, 
  IoAlertCircleOutline 
} from "react-icons/io5";
import type { WorkOrder } from "@/types/WorkOrder";
import { getTimeAgo } from "@/utils/getTimeAgo";

type WorkOrderItemProps = {
  data: WorkOrder;
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "URGENT":
      return "bg-red-100 text-red-700";
    case "HIGH":
      return "bg-orange-100 text-orange-700";
    case "MEDIUM":
      return "bg-yellow-100 text-yellow-700";
    case "LOW":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case "URGENT":
      return "Urgente";
    case "HIGH":
      return "Alta";
    case "MEDIUM":
      return "Média";
    case "LOW":
      return "Baixa";
    default:
      return priority;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "IN_PROGRESS":
      return "bg-blue-100 text-blue-700";
    case "PENDING":
      return "bg-yellow-100 text-yellow-700";
    case "AWAITING_VALIDATION":
      return "bg-purple-100 text-purple-700";
    case "COMPLETED":
      return "bg-green-100 text-green-700";
    case "CANCELLED":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "IN_PROGRESS":
      return "Em Andamento";
    case "PENDING":
      return "Pendente";
    case "AWAITING_VALIDATION":
      return "Aguardando Validação";
    case "COMPLETED":
      return "Concluída";
    case "CANCELLED":
      return "Cancelada";
    default:
      return status;
  }
};

const getSLAStatusColor = (estimatedEndDate: string | null) => {
  if (!estimatedEndDate) return "text-gray-600 bg-gray-50";
  
  const now = new Date();
  const endDate = new Date(estimatedEndDate);
  const diffHours = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  if (diffHours < 0) return "text-red-600 bg-red-50"; // OVERDUE
  if (diffHours < 24) return "text-orange-600 bg-orange-50"; // CRITICAL
  if (diffHours < 48) return "text-yellow-600 bg-yellow-50"; // WARNING
  return "text-green-600 bg-green-50"; // OK
};

export const WorkOrderItem = ({ data }: WorkOrderItemProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/work-orders/${data.id}`, { state: { workOrder: data } });
  };

  // Ordens concluídas ou aguardando validação não devem ser consideradas atrasadas
  const isOverdue = data.status !== "COMPLETED" && data.status !== "CANCELLED" && data.status !== "AWAITING_VALIDATION" && data.estimatedEndDate && new Date(data.estimatedEndDate) < new Date();
  const primaryOccurrence = data.items[0]?.occurrence;

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-2xl p-5 shadow-sm cursor-pointer transition-all hover:shadow-md border-l-4 ${
        isOverdue 
          ? "border-l-red-500 bg-red-50/30" 
          : data.status === "CANCELLED"
          ? "border-l-red-500"
          : data.status === "COMPLETED"
          ? "border-l-green-500"
          : data.status === "AWAITING_VALIDATION"
          ? "border-l-purple-500"
          : data.status === "IN_PROGRESS"
          ? "border-l-blue-500"
          : data.priority === "URGENT"
          ? "border-l-orange-500"
          : "border-l-gray-300"
      }`}
    >
      {/* Header com número e badges */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
              {data.osNumber}
            </span>
            {isOverdue && (
              <span className="text-xs font-bold text-red-600 bg-red-100 px-3 py-1 rounded-full animate-pulse">
                ⚠️ ATRASADA
              </span>
            )}
          </div>
          <h3 className="text-base font-bold text-gray-900 leading-tight">
            {data.title}
          </h3>
        </div>
      </div>

      {/* Status e Prioridade */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span
          className={`px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${getStatusColor(
            data.status
          )}`}
        >
          {getStatusLabel(data.status)}
        </span>
        <span
          className={`px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${getPriorityColor(
            data.priority
          )}`}
        >
          {getPriorityLabel(data.priority)}
        </span>
      </div>

      {/* Informações agrupadas em cards */}
      <div className="space-y-3">
        {/* Categoria e Local */}
        {primaryOccurrence && (
          <div className="bg-gray-50 rounded-xl p-3 space-y-2">
            <div className="flex items-start gap-2">
              <IoAlertCircleOutline className="text-primary mt-0.5 flex-shrink-0 text-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium mb-0.5">Categoria</p>
                <p className="text-sm text-gray-900 font-semibold">
                  {primaryOccurrence.category.name}
                </p>
                <p className="text-xs text-gray-600">
                  {primaryOccurrence.subcategory.name}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
              <IoLocationOutline className="text-primary mt-0.5 flex-shrink-0 text-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium mb-0.5">Localização</p>
                <p className="text-sm text-gray-900 line-clamp-2">{primaryOccurrence.address}</p>
              </div>
            </div>
          </div>
        )}

        {/* Prazo */}
        {data.estimatedEndDate && (
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IoCalendarOutline className="text-primary flex-shrink-0 text-lg" />
                <div>
                  <p className="text-xs text-gray-500 font-medium">Prazo</p>
                  <p className={`text-sm font-bold ${isOverdue ? "text-red-600" : "text-gray-900"}`}>
                    {new Date(data.estimatedEndDate).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    })}
                  </p>
                </div>
              </div>
              {/* Só mostra status do prazo se não estiver concluída ou cancelada */}
              {data.status !== "COMPLETED" && data.status !== "CANCELLED" && (
                <span
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${getSLAStatusColor(
                    data.estimatedEndDate
                  )}`}
                >
                  {isOverdue
                    ? "Vencido"
                    : new Date(data.estimatedEndDate).getTime() - new Date().getTime() < 24 * 60 * 60 * 1000
                    ? "Crítico"
                    : new Date(data.estimatedEndDate).getTime() - new Date().getTime() < 48 * 60 * 60 * 1000
                    ? "Atenção"
                    : "No Prazo"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer com tempo */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
          <IoTimeOutline className="text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-500 font-medium">
            Criada {getTimeAgo(data.createdAt)}
          </span>
          {data.items.length > 0 && (
            <>
              <span className="text-gray-300">•</span>
              <span className="text-xs text-gray-500 font-medium">
                {data.items.length} {data.items.length === 1 ? "ocorrência" : "ocorrências"}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
